import { useState, useRef, useCallback, useEffect } from 'react';
import { useCollaboration } from '@/contexts/CollaborationContext';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Mic, MicOff, Phone, PhoneOff, UserPlus, X } from 'lucide-react';

const AVATAR_COLORS = [
  '#d4a84b', '#14b8a6', '#f97316', '#a855f7', '#ef4444',
  '#3b82f6', '#22c55e', '#ec4899', '#eab308', '#06b6d4'
];

function getColor(id) {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) h = id.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function Avatar({ member, size = 28, showRing = false, ringColor }) {
  const color = getColor(member.user_id);
  const initials = (member.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="relative rounded-full flex items-center justify-center shrink-0 cursor-default"
            style={{
              width: size, height: size,
              backgroundColor: color,
              boxShadow: showRing ? `0 0 0 2px ${ringColor || '#22c55e'}` : undefined,
            }}
            data-testid={`presence-avatar-${member.user_id}`}
          >
            {member.picture ? (
              <img src={member.picture} alt="" className="w-full h-full rounded-full object-cover" />
            ) : (
              <span className="text-white font-bold" style={{ fontSize: size * 0.36 }}>{initials}</span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-[#1a1a1e] border-[#2a2a2e] text-white text-xs px-2 py-1">
          {member.name || 'Anonymous'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function CollaborationBar() {
  const { connected, members, audioMembers, joinAudio, leaveAudio } = useCollaboration();
  const { user } = useAuth();
  const [inCall, setInCall] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showCallPanel, setShowCallPanel] = useState(false);
  const streamRef = useRef(null);

  const self = members.find(m => m.user_id === user?.user_id);
  const others = members.filter(m => m.user_id !== user?.user_id);

  const handleJoinCall = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      joinAudio();
      setInCall(true);
      setShowCallPanel(true);
    } catch {
      joinAudio();
      setInCall(true);
      setMuted(true);
      setShowCallPanel(true);
    }
  }, [joinAudio]);

  const handleLeaveCall = useCallback(() => {
    leaveAudio();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setInCall(false);
    setMuted(false);
    setShowCallPanel(false);
  }, [leaveAudio]);

  const handleToggleMute = useCallback(() => {
    if (streamRef.current) {
      const track = streamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setMuted(!track.enabled);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  if (!connected) return null;

  return (
    <div className="flex items-center gap-2" data-testid="collaboration-bar">
      {/* Figma-style presence avatars — who's on this page */}
      <div className="flex items-center">
        <div className="flex -space-x-2">
          {others.slice(0, 6).map(m => (
            <motion.div
              key={m.user_id}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <Avatar
                member={m}
                size={30}
                showRing={audioMembers.some(am => am.user_id === m.user_id)}
                ringColor="#22c55e"
              />
            </motion.div>
          ))}
          {others.length > 6 && (
            <div
              className="w-[30px] h-[30px] rounded-full bg-[#2a2a2e] flex items-center justify-center text-[10px] text-[#888] font-semibold"
              style={{ border: '2px solid #0a0a0c' }}
            >
              +{others.length - 6}
            </div>
          )}
        </div>

        {/* Self indicator (you) — always last, with a label */}
        {self && (
          <div className="flex items-center gap-1.5 ml-3">
            <Avatar member={self} size={30} showRing={inCall} ringColor="#22c55e" />
            <span className="text-[11px] text-[#666] hidden xl:inline">You</span>
          </div>
        )}
      </div>

      {/* Separator */}
      {members.length > 0 && <div className="w-px h-5 bg-[#2a2a2e] mx-1" />}

      {/* Voice call section */}
      {!inCall ? (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleJoinCall}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1a1a1e] hover:bg-[#22c55e]/10 text-[#888] hover:text-[#22c55e] transition-colors text-xs"
          data-testid="start-call-btn"
        >
          <Phone size={13} />
          <span className="hidden sm:inline">Voice</span>
        </motion.button>
      ) : (
        <div className="flex items-center gap-1">
          {/* Active call indicator */}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#22c55e]/10 border border-[#22c55e]/20">
            <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse" />
            <span className="text-[11px] text-[#22c55e] font-medium">
              {audioMembers.length + 1} in call
            </span>
          </div>

          {/* Mute toggle */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleToggleMute}
            className={`p-1.5 rounded-lg transition-colors ${
              muted ? 'bg-[#f59e0b]/15 text-[#f59e0b]' : 'bg-[#1a1a1e] text-[#aaa] hover:text-white'
            }`}
            data-testid="mute-toggle-btn"
            title={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <MicOff size={14} /> : <Mic size={14} />}
          </motion.button>

          {/* End call */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleLeaveCall}
            className="p-1.5 rounded-lg bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors"
            data-testid="end-call-btn"
            title="Leave call"
          >
            <PhoneOff size={14} />
          </motion.button>
        </div>
      )}

      {/* Call participants panel (shows on hover/click when in call) */}
      <AnimatePresence>
        {inCall && showCallPanel && audioMembers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute top-full right-0 mt-2 w-56 bg-[#141416] border border-[#2a2a2e] rounded-xl p-3 shadow-2xl z-50"
            data-testid="call-participants-panel"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] text-[#888] uppercase tracking-wider font-medium">In this call</span>
              <button onClick={() => setShowCallPanel(false)} className="text-[#555] hover:text-white">
                <X size={12} />
              </button>
            </div>
            <div className="space-y-2">
              {/* Self */}
              <div className="flex items-center gap-2 py-1">
                {self && <Avatar member={self} size={22} showRing ringColor="#22c55e" />}
                <span className="text-xs text-[#ccc]">You</span>
                {muted && <MicOff size={10} className="text-[#f59e0b] ml-auto" />}
              </div>
              {/* Others in call */}
              {audioMembers.map(m => (
                <div key={m.user_id} className="flex items-center gap-2 py-1">
                  <Avatar member={{ user_id: m.user_id, name: m.name }} size={22} showRing ringColor="#22c55e" />
                  <span className="text-xs text-[#ccc]">{m.name}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
